<template lang="pug">
//- .flex-1 fixes short text with img-right not filling a card
template(v-if="screen['text-audio']")
  uc-text(
    :edit-path="`${editPath}.text-audio`"
    :k="`${screen.id}.text-audio`"
    :text="screen['text-audio']"
    :values="textFillValues"
    type="div"
  )
.uc-content.col-span-text.flex.flex-col.gap-6.max-w-full.flex-1(
  :class="[screen.center ? 'items-center mx-auto' : 'items-start', { 'h-full': screen['img-bottom'] }]"
  :data-size="screen['img-size']"
  :uc-dont-read="screen['dont-read'] || null"
  v-else-if="screen.heading || screen.subheading || screen.title || screen.text || screen.instructions || imgTop?.src || imgLRB?.src || (imgHover.available)"
)
  uc-animate.flex-none.flex.items-center.relative(
    :animate="animateImg"
    v-if="imgTop?.src"
  )
    uc-asset(v-bind="imgTop?.assetAttrs?.value" :editPath="`${editPath}.img-top`")
  
  uc-animate.flex-none.flex.items-center.relative(
    :animate="animateImg"
    v-else-if="imgHover.available && imgHover.position === 'top'"
  )
    uc-image-hover(
      v-bind="imgHover?.assetAttrs"
    )
    
  .flex.gap-6.gap-12.lg_gap-18.flex-1.grow-0(
    :class="[screen.center ? 'items-center mx-auto text-center' : 'items-start', { 'fade-in': !(animateHeading || animateSubheading) }]"
    v-if="screen.heading || screen.subheading || screen.title"
  )
    .flex-none.hidden.lg_block(
      :style="{ width: imgLRB?.imgWidth?.value }"
      v-if="screen['img-left']"
    )

    uc-animate(
        :animate="animateHeading || animateSubheading"
      )
      uc-editable(
        :class="twMerge('pb-2 inline-block [text-wrap:balance]', screen.subheading ? 'subheading' : 'heading', screen.styles?.title, screen.styles?.heading, screen.styles?.subheading)"
        :editPath="`${editPath}.${screen.heading ? 'heading' : screen.subheading ? 'subheading' : screen.title ? 'title' : null}`"
        :k="screen.heading ? `${screen.id}.heading` : screen.subheading ? `${screen.id}.subheading` : `${screen.id}.title`"
        :text="screen.heading || screen.subheading || screen.title"
        :type="screen.h ? screen.h : screen.subheading ? 'h4' : 'h3'"
        :values="textFillValues"
      )

  .flex.flex-col.gap-6.gap-12.lg_gap-18.w-full(
    :class="[imgFlexClass, {'h-full': screen['img-bottom'] || imgHover.position === 'bottom'}, {'fade-in': !animateText} ]"
  )
    
    uc-animate(
      :animate="animateText"
    )
      .flex.flex-col.gap-6.flex-1(
        :class="[screen.center ? 'items-center mx-auto' : 'items-start']"
        v-if="screen.text || screen.instructions"
      )
        uc-editable(
          :class="screen.styles?.text"
          :editPath="`${editPath}.text`"
          :k="screen.id + '.text'"
          :text="screen.text"
          :values="textFillValues"
          class="flex-[3]"
          v-if="screen.text"
        ) 

        uc-editable.italic.instructions(
          :class="screen.styles?.instructions"
          :editPath="`${editPath}.instructions`"
          :k="screen.id + '.instructions'"
          :text="screen.instructions"
          :values="textFillValues"
          uc-word-italic=""
          v-if="screen.instructions && showInstructions"
        )

    uc-animate.flex-none.flex.items-center.relative(
      :animate="animateImg"
      :parallax="imgLRB.parallax.value"
      v-if="imgLRB.src"
    )
      uc-asset.self-baseline(v-bind="{ ...imgLRB?.assetAttrs?.value }" :editPath="`${editPath}.${imgLRB?.editProp?.value}`")

    uc-animate.flex-none.flex.items-center.relative(
      :animate="animateImg"
      v-else-if="imgHover.available && imgHover.position !== 'top'"
    )
      uc-image-hover(
        v-bind="imgHover?.assetAttrs"
      )

.wrapper-col(v-if="screen.templates")
  uc-screen(
    :edit-path="`${editPath}.templates.${i}`"
    :nested="true"
    :screen="childScreenWithPlaceholders(s)"
    v-for="(s, i) in screen.templates"
  )
</template>

<script setup lang="ts">
import { twMerge } from '../lib/mergeStyles';
import { ref, computed, onMounted, onBeforeUnmount, inject } from 'vue';
import isNumber from '../lib/isNumber';
import useTheme from '../stores/useTheme';
import { withInheritedPlaceholderValues } from '../lib/useScreenAudio';

const content: any = inject('content');

const props = defineProps<{ screen: UcContent; editPath: string }>();

const theme = useTheme();

const textFillValues = computed((): Record<string, string | number> => {
  const v = props.screen['placeholder-values'];
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  return v as Record<string, string | number>;
});

function childScreenWithPlaceholders(s: UcScreen): UcScreen {
  return withInheritedPlaceholderValues(
    s as unknown as Record<string, unknown>,
    props.screen as unknown as Record<string, unknown>
  ) as UcScreen;
}

const animateImg = computed(
  () =>
    props.screen?.['animate-img'] ??
    (content.value.course?.['animate-img'] ||
      theme.value?.['animate-img'] ||
      '')
);

const animateText = computed(
  () =>
    props.screen?.['animate-text'] ??
    (content.value.course?.['animate-text'] ||
      theme.value?.['animate-text'] ||
      '')
);

const animateHeading = computed(
  () =>
    props.screen?.['animate-heading'] ??
    (content.value.course?.['animate-heading'] ||
      theme.value?.['animate-heading'] ||
      '')
);

const animateSubheading = computed(
  () =>
    props.screen?.['animate-subheading'] ??
    (content.value.course?.['animate-subheading'] ||
      theme.value?.['animate-subheading'] ||
      '')
);

const imgFlexClass = computed(() => {
  if (props.screen['img-right'] || imgHover.position === 'right')
    return 'lg_flex-row';
  else if (props.screen['img-left'] || imgHover.position === 'left')
    return 'lg_flex-row-reverse';
  return undefined;
});

const useImg = ({ forTop = false } = {}) => {
  const srcRef = computed(() =>
    forTop
      ? props.screen['img-top']
      : props.screen.img ||
        props.screen['img-left'] ||
        props.screen['img-right'] ||
        props.screen['img-bottom']
  );

  const editProp = computed(() => {
    if (forTop) return 'img-top';
    if (props.screen.img) return 'img';
    if (props.screen['img-left']) return 'img-left';
    if (props.screen['img-right']) return 'img-right';
    if (props.screen['img-bottom']) return 'img-bottom';
    return undefined;
  });

  const parallax = computed(() =>
    forTop ? props.screen['img-parallax-top'] : props.screen['img-parallax']
  );

  if (!srcRef.value) {
    return {};
  }

  const getSize = () => {
    const src = srcRef.value;
    const defaultImgSize = isNumber(src) ? 3 : 16;
    const topImageSize = forTop && props.screen['img-size-top'];
    const finalImgSize =
      topImageSize || props.screen['img-size'] || defaultImgSize;

    return finalImgSize;
  };

  const getAlt = () => {
    const altProp = forTop
      ? props.screen['img-alt-top']
      : props.screen['img-alt'];

    return altProp || '';
  };

  const assetAttrs = computed(() => {
    const src = srcRef.value;

    return {
      class: twMerge('mx-auto', props.screen?.styles?.img),
      size: getSize(),
      alt: getAlt(),
      src
    };
  });

  const imgWidth = computed(() => {
    const finalImgSize = getSize();
    return finalImgSize ? finalImgSize + 'rem' : undefined;
  });

  return { src: srcRef, assetAttrs, imgWidth, parallax, editProp };
};

const useImgHover = () => {
  const imgHover = computed(() => props.screen['img-hover']);

  if (!imgHover.value) return {};

  const assetAttrs = {
    alt: imgHover.value.alt,
    class: twMerge('mx-auto', props.screen?.styles?.img),
    effect: imgHover.value.effect,
    base: imgHover.value.base,
    over: imgHover.value.over,
    size: imgHover.value.size ?? 16,
    baseStyle: imgHover.value?.['base-style'],
    overStyle: imgHover.value?.['over-style']
  };

  return {
    available: assetAttrs.over && assetAttrs.base,
    position: imgHover.value.position ?? 'bottom',
    assetAttrs
  };
};

const imgTop = useImg({ forTop: true });
const imgLRB = useImg();
const imgHover = useImgHover();

const windowSize = ref({
  width: window.innerWidth,
  height: window.innerHeight
});

const updateWindowSize = () => {
  windowSize.value = {
    width: window.innerWidth,
    height: window.innerHeight
  };
};

const showInstructions = computed(() => {
  // Force recomputation with windowSize dependency
  const { width, height } = windowSize.value;
  const element = document.querySelector(`#${props.screen.id}`);

  if (element) {
    const hasScreenTabs = element.querySelector('.screen-tabs');
    const hasScreenBlinds = element.querySelector('.screen-blinds');
    const hasMobileLayout = element.querySelector('.mobile-layout');
    if ((hasScreenTabs || hasScreenBlinds) && hasMobileLayout) {
      return false;
    }
  }

  return true;
});

onMounted(() => {
  window.addEventListener('resize', updateWindowSize);
  updateWindowSize();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWindowSize);
});
</script>
